import { useEffect, useMemo, useState } from "react";
import { initialValues as initialValuesBase } from "./config";
import { ORG_TYPE, Organization } from "types/organization";
import {
  useAddOrganizationMutation,
  useAddOrganizationSitesMutation,
  useAddOrganizationUsersMutation,
  useDeleteOrganizationSiteByOrgIdAndSiteIdMutation,
  useDeleteOrganizationUserByOrgIdAndUserIdMutation,
  useGetOrganizationByIdLazyQuery,
  useGetUsersQuery,
  useUpdateOrganizationMutation,
  useGetSitesQuery,
  useAddProgramMutation,
  useGetOrganizationDetailByIdLazyQuery,
  useGetOrgCustomLabelsByOrgLazyQuery,
  GetOrganizationsDocument,
} from "graphql/__generated__/hooks";
import { Messages, NotificationSeverity } from "helpers/constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { uploadFile } from "reduxStore/files/thunks";
import { useNavigate, useParams } from "react-router-dom";
import { ROLES, User } from "types/user";
import { AdminRoutes } from "config/routes";
import { useNotifications } from "hooks/useNotifications";
import { Site } from "types/site";
import { setOrganizationsThunk } from "reduxStore/organization/thunks";
import { organizationSelector } from "reduxStore/organization/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { useApolloClient } from "@apollo/client";

export const useCreateOrEditOrganization = () => {
  const { notify } = useNotifications();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const organizationsFromStore = useAppSelector(organizationSelector);

  const isEditMode = !!organizationId;
  const [addOrgUsers] = useAddOrganizationUsersMutation();
  const [addOrgSites] = useAddOrganizationSitesMutation();
  const [deleteOrgUser] = useDeleteOrganizationUserByOrgIdAndUserIdMutation();
  const [deleteOrgSite] = useDeleteOrganizationSiteByOrgIdAndSiteIdMutation();
  const [addProgram] = useAddProgramMutation();
  const [getOrganization, { data: dataOrganization }] =
    useGetOrganizationByIdLazyQuery();
  const [getOrganizationDetail, { data: dataOrganizationDetail }] =
    useGetOrganizationDetailByIdLazyQuery();
  const [getCustomLabels, { data: dataCustomLabels }] =
    useGetOrgCustomLabelsByOrgLazyQuery();

  const organization = useMemo(() => {
    if (dataOrganization) {
      return dataOrganization.organization_by_pk;
    }
  }, [dataOrganization]);

  const isClinical = useMemo(
    () => organization?.type === ORG_TYPE.CLINIC,
    [organization],
  );

  const customLabels = useMemo(() => {
    if (dataCustomLabels) {
      return dataCustomLabels.organization_custom_label.map(
        (customLabel) => customLabel.custom_label,
      );
    } else {
      return (
        dataOrganization?.organization_by_pk?.custom_labels?.map(
          (customLabel) => customLabel.custom_label,
        ) || []
      );
    }
  }, [dataOrganization, dataCustomLabels]);

  const { data: userData } = useGetUsersQuery();
  const { data: sitesData } = useGetSitesQuery();

  const users = useMemo(() => {
    return userData?.user || [];
  }, [userData]);

  const allSites = useMemo(() => {
    return sitesData?.site || [];
  }, [sitesData]);

  useEffect(() => {
    getOrganization({ variables: { organizationId } }).then();
    if (organizationId) {
      getOrganizationDetail({ variables: { organizationId } }).then();
    }
  }, [organizationId]);

  useEffect(() => {
    const { existingCoaches, existingDirectors, existingManagers } =
      getExistingOrgUsers();
    setImg(organization?.logo_url ?? "");
    setDirectors(existingDirectors || []);
    setManagers(existingManagers || []);
    setCoaches(existingCoaches || []);
    const existingSites = organization?.sites?.map(
      (siteContainer) => siteContainer.site as Site,
    );
    setSites(existingSites || []);
  }, [organization]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorImage, setErrorImage] = useState("");

  const [sites, setSites] = useState<Site[]>([]);
  const [directors, setDirectors] = useState<User[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);

  const [img, setImg] = useState(organization?.logo_url ?? "");
  const [addOrganization] = useAddOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const dispatch = useAppDispatch();

  const hasProgram = useMemo(() => {
    return (dataOrganizationDetail?.program?.length || 0) > 0;
  }, [dataOrganizationDetail]);

  const handleAddProgram = async () => {
    if (!organizationId) return;
    await addProgram({ variables: { orgId: organizationId } });
    notify({
      message: "Program added successfully",
      severity: NotificationSeverity.success,
    });
    await getOrganizationDetail({ variables: { organizationId } });
  };

  const handleCreateOrEditOrganization = async (reqValues: Organization) => {
    setLoading(true);
    const { ferpa_school, school_officials_accepted_at, ...createValues } =
      reqValues;
    const newOrgResult = await addOrganization({
      variables: createValues,
      refetchQueries: [{ query: GetOrganizationsDocument }],
      awaitRefetchQueries: true,
    });
    const created =
      newOrgResult.data?.insert_organization?.returning[0];
    const orgId = created?.org_id;
    if (orgId) {
      await updateOrganization({
        variables: {
          id: orgId,
          organization: { ferpa_school, school_officials_accepted_at },
        },
      });
    }
    await syncOrgUsers(orgId);
    await syncOrgSites(orgId);
    await addProgram({ variables: { orgId } });

    // Refresh the shared org list immediately so the sidebar select updates
    // without requiring a full page refresh.
    try {
      const refreshed = await apolloClient.query({
        query: GetOrganizationsDocument,
        fetchPolicy: "network-only",
      });
      if (refreshed.data?.organization) {
        dispatch(
          setOrganizationsThunk(
            refreshed.data.organization as Organization[],
          ),
        );
      } else if (created && organizationsFromStore) {
        dispatch(
          setOrganizationsThunk([
            ...organizationsFromStore,
            {
              ...reqValues,
              org_id: created.org_id,
            } as Organization,
          ]),
        );
      }
    } catch {
      // List page will still reload orgs; sidebar may need a soft refresh.
    }

    navigate(AdminRoutes.OrganizationList);
    setLoading(false);
    notify({
      message: Messages.successOrganizationCreated,
      severity: NotificationSeverity.success,
    });
  };

  const handleEditOrganization = async (reqValues: Organization) => {
    setLoading(true);
    await updateOrganization({
      variables: {
        id: organization?.org_id || "",
        organization: reqValues,
      },
    });
    await syncOrgUsers(organization?.org_id);
    await syncOrgSites(organization?.org_id);

    navigate(AdminRoutes.OrganizationList);
    setLoading(false);
    notify({
      message: Messages.successOrganizationUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const getExistingOrgUsers = () => {
    const existingDirectors = organization?.users
      ?.filter((user) => user?.user?.role === ROLES.DIRECTOR)
      ?.map((userContainer) => userContainer.user as User);
    const existingManagers = organization?.users
      ?.filter((user) => user?.user?.role === ROLES.MANAGER)
      ?.map((userContainer) => userContainer.user as User);
    const existingCoaches = organization?.users
      ?.filter((user) => user?.user?.role === ROLES.COACH)
      ?.map((userContainer) => userContainer.user as User);
    return { existingCoaches, existingDirectors, existingManagers };
  };

  const getUserToAddAndDelete = (existingUsers: User[], newUsers: User[]) => {
    const usersToAdd = newUsers.filter(
      (newUser) =>
        !existingUsers.some(
          (existingUser) => existingUser.user_id === newUser.user_id,
        ),
    );
    const usersToDelete = existingUsers.filter(
      (existingUser) =>
        !newUsers.some((newUser) => newUser.user_id === existingUser.user_id),
    );
    return { usersToAdd, usersToDelete };
  };

  const getSitesToAddAndDelete = (existingSites: Site[], newSites: Site[]) => {
    const sitesToAdd = newSites.filter(
      (newSite) =>
        !existingSites.some(
          (existingUser) => existingUser.site_id === newSite.site_id,
        ),
    );
    const sitesToDelete = existingSites.filter(
      (existingSite) =>
        !newSites.some((newUser) => newUser.site_id === existingSite.site_id),
    );
    return { sitesToAdd, sitesToDelete };
  };

  const syncOrgSites = async (orgId: string) => {
    const existingSites = organization?.sites?.map(
      (siteContainer) => siteContainer.site as Site,
    );

    const { sitesToAdd, sitesToDelete } = getSitesToAddAndDelete(
      existingSites || [],
      sites,
    );

    const sitesToAddData = sitesToAdd.map((site) => ({
      org_id: orgId,
      site_id: site.site_id,
    }));
    await addOrgSites({
      variables: {
        objects: sitesToAddData,
      },
    });
    for (const siteToDelete of sitesToDelete) {
      await deleteOrgSite({
        variables: {
          org_id: orgId,
          site_id: siteToDelete.site_id,
        },
      });
    }
  };

  const syncOrgUsers = async (orgId: string) => {
    const { existingCoaches, existingDirectors, existingManagers } =
      getExistingOrgUsers();
    const { usersToAdd: directorsToAdd, usersToDelete: directorsToDelete } =
      getUserToAddAndDelete(existingDirectors || [], directors);
    const { usersToAdd: managersToAdd, usersToDelete: managersToDelete } =
      getUserToAddAndDelete(existingManagers || [], managers);
    const { usersToAdd: coachesToAdd, usersToDelete: coachesToDelete } =
      getUserToAddAndDelete(existingCoaches || [], coaches);
    const usersToDelete = [
      ...directorsToDelete,
      ...managersToDelete,
      ...coachesToDelete,
    ];
    const usersToAdd = [...directorsToAdd, ...managersToAdd, ...coachesToAdd];
    const usersToAddData = usersToAdd.map((user) => ({
      org_id: orgId,
      user_id: user.user_id,
    }));
    await addOrgUsers({
      variables: {
        objects: usersToAddData,
      },
    });
    for (const userToDelete of usersToDelete) {
      await deleteOrgUser({
        variables: {
          org_id: orgId,
          user_id: userToDelete.user_id,
        },
      });
    }
  };

  const initialValues = isEditMode
    ? {
        organizationType: organization?.type ?? "",
        organizationName: organization?.name ?? "",
        primaryContactName: organization?.primary_contact_name ?? "",
        primaryContactEmail: organization?.primary_contact_email ?? "",
        secondaryContactName: organization?.secondary_contact_name ?? "",
        secondaryContactEmail: organization?.secondary_contact_email ?? "",
        ferpaSchool:
          organization?.ferpa_school === true
            ? "yes"
            : organization?.ferpa_school === false
              ? "no"
              : "",
        acceptSchoolOfficials: Boolean(
          organization?.school_officials_accepted_at,
        ),
      }
    : initialValuesBase;

  const onUploadFile = async (event: React.BaseSyntheticEvent) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setErrorImage(Messages.errorInvalidImageType);
      return;
    }
    if (file.size > 1024 * 1024 * 10) {
      setErrorImage(Messages.errorImageSize);
      return;
    }
    if (errorImage !== "") setErrorImage("");
    setUploading(true);
    const result = await dispatch(uploadFile(event.target.files[0]));
    if (result.meta.requestStatus === "rejected") {
      setErrorImage(Messages.errorImageUpload);
    } else {
      setImg(result?.payload?.filePath);
    }
    setUploading(false);
  };

  const fetchCustomLabels = async () => {
    if (organizationId) {
      await getCustomLabels({ variables: { orgId: organizationId } });
    }
  };

  return {
    onUploadFile,
    uploading,
    img,
    errorImage,
    isEditMode,
    hasProgram,
    organization,
    users,
    allSites,
    sites,
    directors,
    setSites,
    setDirectors,
    managers,
    setManagers,
    coaches,
    setCoaches,
    loading,
    handleCreateOrEditOrganization,
    handleEditOrganization,
    handleAddProgram,
    initialValues,
    customLabels,
    organizationId,
    isClinical,
    fetchCustomLabels,
  };
};
